import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import {
  deepFreeze,
  formatCurrency,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { every, get, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

/**
 * A manual adjustment to an employee's held pay balance, made by an
 * admin or payroll user. Stored at:
 *   users/{userID}/heldPayAdjustments/{adjustmentID}
 *
 * The `amount` field is the delta applied to the employee's held pay
 * balance (the amount the company owes the employee in held pay):
 *   - positive  => INCREASE held pay (company now owes employee more)
 *   - negative  => DECREASE held pay (e.g. write-off / unrecorded payout)
 *
 * Internally, the held pay balance lives in `balances/{userID}.total` as
 * a NEGATIVE number when the company owes the employee, so a positive
 * adjustment here translates into a negative delta on `balance.total`.
 * The Cloud Function trigger handles that translation; clients should
 * always think in terms of "held pay balance".
 */
export const HELD_PAY_ADJUSTMENT_FIELDS = {
  amount: 0,
  reason: "",
  // The sale this adjustment is charged against, as `projectID + saleID`
  // (the same key a Commission doc is stored under, so the two join
  // directly). Justin: clawbacks "need to be tracked by sale PO just like
  // commission". Craig: "we need to keep clear records of the clawbacks so
  // salespeople know why their pay was reduced."
  //
  // Null for ordinary adjustments that are not tied to a sale. Required by
  // the UI when a decrease takes the balance below zero, since that IS the
  // clawback case.
  saleID: null,
  // Snapshot of the employee's held pay balance immediately before this
  // adjustment was applied (positive number = held pay owed). Useful for
  // a clean ledger view without having to recompute history.
  balanceBefore: null,
  balanceAfter: null,
  // Meta info
  created: null,
  createdBy: null,
};
deepFreeze(HELD_PAY_ADJUSTMENT_FIELDS);

export default class HeldPayAdjustment extends DataStructure {
  constructor(userID, id, data = {}) {
    data = sanitize(data, HELD_PAY_ADJUSTMENT_FIELDS);
    data.created = timestampToDate(data.created);
    super(data);

    this.id = id || null;
    this.userID = userID;
    this.permissions = Object.freeze({
      view: HeldPayAdjustment.canBeViewedBy,
      create: HeldPayAdjustment.canBeCreatedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user, userID) {
    if (!user) return false;
    if (user.isAdmin) return true;
    if (user.isEmployee && user.hasRole && user.hasRole("payroll")) return true;
    // Employees can view their own held pay history
    return Boolean(userID && user.id === userID);
  }

  static canBeCreatedBy(user) {
    if (!user) return false;
    return (
      user.isAdmin ||
      (user.isEmployee && user.hasRole && user.hasRole("payroll"))
    );
  }

  // Database

  get path() {
    return HeldPayAdjustment.getPath(this.userID, this.id);
  }

  static getPath(userID, id) {
    let path = `users/${userID}/heldPayAdjustments`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(adjustment) {
    return adjustment.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new HeldPayAdjustment(
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      amount: [
        (a) =>
          (typeof a === "number" && Number.isFinite(a) && a !== 0) ||
          "amountRequired",
      ],
      reason: [(a) => Boolean(a && a.trim()) || "required"],
      saleID: [(a) => a === null || typeof a === "string" || "invalid"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return Boolean(
      !this.id && isEmpty(diff(this.sanitize(), HELD_PAY_ADJUSTMENT_FIELDS))
    );
  }

  isEqual(other) {
    return Boolean(
      this.id === other.id && isEmpty(diff(this.sanitize(), other.sanitize()))
    );
  }

  sanitize() {
    this.data.amount = roundPrice(this.data.amount || 0);
    if (typeof this.data.reason === "string") {
      this.data.reason = this.data.reason.trim();
    }
    this.data.saleID = this.data.saleID || null;
    return this.data;
  }

  // Formatting

  get user() {
    return store.state.main.users[this.userID] || null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  /**
   * Signed amount as a formatted currency string with a leading sign.
   * Example: "+$850.00", "-$50.00".
   */
  get amountFormatted() {
    const amount = this.data.amount || 0;
    return (
      (amount > 0 ? "+" : amount < 0 ? "-" : "") +
      formatCurrency(Math.abs(amount))
    );
  }

  get isIncrease() {
    return (this.data.amount || 0) > 0;
  }

  /**
   * The sale this was charged against, if any. Adjustments live under the
   * user, so this resolves through the sales the caller already has loaded.
   */
  get sale() {
    return this.data.saleID
      ? store.state.main.sales[this.data.saleID] || null
      : null;
  }
}
